import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Sparkles, ExternalLink, CheckCircle2, Trash2, BotMessageSquare } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const MODEL_OPTIONS = [
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Empfohlen)" },
  { value: "gpt-4o", label: "GPT-4o" },
  { value: "gpt-4o-mini", label: "GPT-4o mini (schnell)" },
  { value: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

const CATEGORIES = ["News", "Jubiläum", "Retro", "Glitch", "Community", "Rare", "Event"];

const formatDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

const AiNewsPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [category, setCategory] = useState("News");
  const [busyGuid, setBusyGuid] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [hotel, setHotel] = useState("de");
  const [feedError, setFeedError] = useState(null);

  const load = async (h = hotel) => {
    setLoading(true);
    setFeedError(null);
    try { setItems(await a.aiNewsFeed(h)); }
    catch (err) {
      const msg = err?.response?.data?.detail || "RSS nicht erreichbar";
      setFeedError(msg);
      setItems([]);
      toast({ title: "Feed-Fehler", description: msg, variant: "destructive" });
    }
    finally { setLoading(false); }
  };

  // Hotel-Liste einmalig laden
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await a.aiNewsHotels();
        if (!cancelled && Array.isArray(h)) setHotels(h);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [a]);

  useEffect(() => { load(hotel); }, [hotel]);

  const onHotelChange = (h) => setHotel(h);

  const onImport = async (it) => {
    setBusyGuid(it.guid);
    try {
      const res = await a.aiNewsImport({
        guid: it.guid,
        title: it.title,
        description: it.description,
        link: it.link,
        pub_date: it.pub_date,
        category,
        model,
        hotel,
      });
      toast({ title: "Importiert", description: `Draft: "${res.title}" – wartet auf Freigabe` });
      await load(hotel);
    } catch (err) {
      toast({
        title: "Import fehlgeschlagen",
        description: err?.response?.data?.detail || "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally { setBusyGuid(null); }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Import "${it.title}" zuruecksetzen?`)) return;
    try {
      await a.aiNewsDeleteImport(it.guid);
      toast({ title: "Import zurueckgesetzt" });
      await load(hotel);
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  return (
    <div data-testid="ai-news-panel" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-white">
            <BotMessageSquare className="h-5 w-5 text-[#b39bff]" /> KI-News-Import
          </h2>
          <p className="mt-1 text-sm text-zinc-400">
            Importiere offizielle Habbo-News (RSS) – jetzt aus allen Hotels inkl. Origins – schreib sie im Habthek-Stil um und sende sie als Draft in die Freigabe.
          </p>
        </div>
        <Button onClick={() => load(hotel)} className="btn-ghost rounded-lg px-3 py-2 text-sm" data-testid="ai-news-refresh">
          <RefreshCw className="mr-2 h-4 w-4" /> Feed aktualisieren
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-[#0f141b] p-4 md:grid-cols-[220px_200px_200px_1fr]">
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Habbo Hotel</label>
          <select
            data-testid="ai-news-hotel"
            value={hotel}
            onChange={(e) => onHotelChange(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
          >
            {(hotels.length ? hotels : [{ id: "de", label: "Habbo Deutschland", flag: "🇩🇪" }]).map((h) => (
              <option key={h.id} value={h.id}>{h.flag} {h.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">KI-Modell</label>
          <select
            data-testid="ai-news-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
          >
            {MODEL_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Kategorie</label>
          <select
            data-testid="ai-news-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="self-end text-[11px] text-zinc-500">
          Jeder Import wird als Draft mit <span className="text-[#b39bff]">needs_review = true</span> gespeichert. Reviewer (Permission <em>news_review</em>) entscheiden ueber die Freigabe.
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : feedError ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-6 text-center text-sm text-rose-200">
          <div className="font-semibold mb-1">Feed nicht abrufbar</div>
          <div className="text-xs text-rose-200/80">{feedError}</div>
          <div className="mt-2 text-[11px] text-zinc-400">
            Hinweis: Nicht jedes Hotel betreibt einen RSS-Feed. Probier ein anderes Hotel oder spaeter erneut.
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] p-10 text-center text-sm text-zinc-500">
          Aktuell keine Habbo-News im Feed.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => {
            // Extrahiert Host fuer "Original auf habbo.X" Label aus dem Link
            let originHost = "habbo.de";
            try {
              originHost = new URL(it.link).host.replace(/^www\./, "");
            } catch { /* keep default */ }
            return (
            <div
              key={`${it.guid}-${idx}`}
              data-testid={`ai-news-item-${it.guid.slice(-40)}`}
              className={`relative grid grid-cols-1 gap-3 rounded-xl border bg-[#0f141b] p-4 md:grid-cols-[1fr_auto] ${it.is_new ? "border-emerald-500/40" : "border-white/10"}`}
            >
              {it.is_new && (
                <span
                  className="pointer-events-none absolute -top-2 -right-2 z-10 inline-flex items-center gap-0.5 rounded-md border border-emerald-400/60 bg-gradient-to-br from-emerald-500 to-teal-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-lg shadow-emerald-500/40"
                  title="Neu im RSS-Feed (letzte 48h)"
                  data-testid={`ai-news-new-${it.guid.slice(-40)}`}
                >
                  Neu
                </span>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{formatDate(it.pub_date)}</span>
                  {it.already_imported && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                      <CheckCircle2 className="h-3 w-3" /> Importiert
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm font-semibold text-white">{it.title}</div>
                <div className="mt-1 text-xs text-zinc-400">{it.description}</div>
                <a href={it.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#b39bff] hover:underline">
                  Original auf {originHost} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="flex items-center justify-end gap-2">
                {it.already_imported ? (
                  <Button
                    onClick={() => onDelete(it)}
                    className="btn-ghost rounded-lg px-3 py-2 text-xs text-rose-300 hover:border-rose-500/40"
                    data-testid={`ai-news-delete-${it.guid.slice(-40)}`}
                  >
                    <Trash2 className="mr-1 h-4 w-4" /> Draft loeschen
                  </Button>
                ) : (
                  <Button
                    onClick={() => onImport(it)}
                    disabled={busyGuid === it.guid}
                    className="btn-primary rounded-lg px-3 py-2 text-xs"
                    data-testid={`ai-news-import-${it.guid.slice(-40)}`}
                  >
                    {busyGuid === it.guid ? (
                      <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> KI schreibt...</>
                    ) : (
                      <><Sparkles className="mr-1 h-4 w-4" /> KI-Import</>
                    )}
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiNewsPanel;
